// built-from: /client-api-via-hooks v1 impl:client-api-via-hooks role:implementation
// built-from: /shared-process-spine v1 impl:shared-process-spine role:implementation
// EngagementProvider + useEngagement hook (spec 027-engagement-aggregate, Slice B,
// FR-009/FR-011, SC-004, User Story 3).
//
// The engagement is the aggregate root: it holds the vision / organisation /
// streams sub-documents centrally. This provider is the ONLY caller of the
// /api/engagements surface (constitution III: the UI reaches the API through a
// hook/provider, never `fetch` inside a component). It mirrors
// useVisionFraming.ts's degradation discipline: it NEVER throws; on a store /
// fetch / network error it resolves to a SAFE state (no current engagement,
// snapshot null) so the synchronous readers keep their empty-shape floor.
//
// SNAPSHOT SEAM (the bridge to the pure readers): a MODULE-LEVEL snapshot the
// provider SETS, read SYNCHRONOUSLY by readVision()/readOrgContext()/readScope()
// via getEngagementSnapshot(). This keeps those readers synchronous (no React
// hook inside a pure util) with the smallest blast radius. The provider calls
// setEngagementSnapshot(...) whenever the current engagement resolves / switches
// / saves, and setEngagementSnapshot(null) when there is no current engagement.
//
// FR-011: 'arcpilot.engagementId' is the ONLY localStorage key this provider
// writes. It NEVER writes arcpilot.vision/orgContext/scope.

import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { importLegacyLocalStorage } from '../utils/legacyImport';

// ── The three sub-document shapes of an engagement (kept opaque here; the pure
//    readers parse them defensively). ─────────────────────────────────────────
export interface EngagementSnapshot {
  vision: unknown;
  organisation: unknown;
  streams: unknown;
  // 032: the knowledge-repo link sub-doc the readers read over. OPTIONAL +
  // present only when the resolved engagement actually carries a repoLink, so
  // the 027 three-sub-doc snapshot shape is preserved verbatim for legacy
  // engagements (additive — the existing snapshot stays the floor).
  repoLink?: unknown;
  processSpines?: unknown;
  activitySessions?: unknown;
  setupRevision?: string | null;
}

export interface EngagementSummary {
  id: string;
  client: string;
  programme: string;
  title?: string;
  lifecycle?: 'draft' | 'active';
  activationState?: 'draft' | 'projecting' | 'active';
  createdAt?: string;
}

export interface Engagement extends EngagementSummary {
  updatedAt?: string;
  vision: unknown;
  organisation: unknown;
  streams: unknown;
  repoLink?: unknown;
  processSpines?: unknown;
  activitySessions?: unknown;
  setupRevision?: string | null;
}

export interface UseEngagementReturn {
  loading: boolean;
  engagements: EngagementSummary[];
  currentEngagement: Engagement | null;
  currentEngagementId: string | null;
  snapshot: EngagementSnapshot | null;
  isFirstRun: boolean;
  createEngagement: (input: {
    client: string;
    programme?: string;
    title?: string;
    commandId?: string;
    flow?: 'brief';
  }) => Promise<Engagement | null>;
  switchEngagement: (id: string) => Promise<boolean>;
  // 041 (F6): re-read the CURRENT engagement from the server and re-publish its
  // snapshot. The vision capability hook calls this after a successful
  // server-side capability so the form + chat both re-read the one `vision`
  // sub-doc (SC-003 shared state) rather than holding an authoritative copy.
  refreshEngagement: () => Promise<void>;
  saveVision: (vision: unknown) => Promise<void>;
  saveOrganisation: (organisation: unknown) => Promise<void>;
  saveStreams: (streams: unknown) => Promise<void>;
  changeProcessSpine: (
    command: Record<string, unknown> & { expectedRevision: number },
  ) => Promise<boolean>;
}

const POINTER_KEY = 'arcpilot.engagementId';

// Empty sub-document shapes (match the Slice A server contract).
const EMPTY_VISION = { goals: [] };
const EMPTY_ORG = { organisation: null, background: null, painPoints: [] };
const EMPTY_STREAMS = { streams: [] };

// ── Module-level synchronous SNAPSHOT seam (mechanism #1) ─────────────────────
let moduleSnapshot: EngagementSnapshot | null = null;

export function getEngagementSnapshot(): EngagementSnapshot | null {
  return moduleSnapshot;
}

export function setEngagementSnapshot(s: EngagementSnapshot | null): void {
  moduleSnapshot = s;
}

function snapshotOf(eng: Engagement): EngagementSnapshot {
  const snap: EngagementSnapshot = {
    vision: eng.vision ?? EMPTY_VISION,
    organisation: eng.organisation ?? EMPTY_ORG,
    streams: eng.streams ?? EMPTY_STREAMS,
  };
  // Include repoLink ONLY when the engagement carries one (032): a legacy
  // engagement with no repoLink keeps the exact three-sub-doc snapshot shape;
  // the readers fall back to the unlinked floor for an absent field.
  if (eng.repoLink !== undefined && eng.repoLink !== null) {
    snap.repoLink = eng.repoLink;
  }
  if (eng.processSpines !== undefined) snap.processSpines = eng.processSpines;
  if (eng.activitySessions !== undefined) snap.activitySessions = eng.activitySessions;
  if (eng.setupRevision !== undefined) snap.setupRevision = eng.setupRevision;
  return snap;
}

// ── localStorage pointer helpers (jsdom/SSR-safe; never throw) ────────────────
function readPointer(): string | null {
  try {
    return window.localStorage.getItem(POINTER_KEY);
  } catch {
    return null;
  }
}

function writePointer(id: string): void {
  try {
    window.localStorage.setItem(POINTER_KEY, id);
  } catch {
    // storage unavailable → no-op, never throws.
  }
}

function clearPointer(): void {
  try {
    window.localStorage.removeItem(POINTER_KEY);
  } catch {
    // no-op
  }
}

// ── Thin fetch helpers (never throw; resolve null on any failure) ─────────────
async function fetchJson<T>(url: string, init?: RequestInit): Promise<T | null> {
  try {
    const res = await fetch(url, init);
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

function getList(): Promise<EngagementSummary[] | null> {
  return fetchJson<EngagementSummary[]>('/api/engagements');
}

function getById(id: string): Promise<Engagement | null> {
  return fetchJson<Engagement>(`/api/engagements/${encodeURIComponent(id)}`);
}

function postCreate(input: {
  client: string;
  programme?: string;
  title?: string;
  commandId?: string;
  flow?: 'brief';
}): Promise<Engagement | null> {
  return fetchJson<Engagement>('/api/engagements', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

function putSubDoc(
  id: string,
  sub: 'vision' | 'organisation' | 'streams',
  body: unknown,
): Promise<unknown> {
  return fetchJson(`/api/engagements/${encodeURIComponent(id)}/${sub}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

function processCommandId(): string {
  try {
    return `process-spine-${crypto.randomUUID()}`;
  } catch {
    return `process-spine-${Date.now().toString(36)}`;
  }
}

// ── Context ───────────────────────────────────────────────────────────────────
const EngagementContext = createContext<UseEngagementReturn | null>(null);

export function EngagementProvider({ children }: { children?: ReactNode }): JSX.Element {
  const [loading, setLoading] = useState(true);
  const [engagements, setEngagements] = useState<EngagementSummary[]>([]);
  const [currentEngagement, setCurrentEngagement] = useState<Engagement | null>(null);
  const [snapshot, setSnapshot] = useState<EngagementSnapshot | null>(null);
  const [isFirstRun, setIsFirstRun] = useState(false);

  // Avoid a state update after unmount (the load effect is async).
  const mountedRef = useRef(true);
  const requestGenerationRef = useRef(0);
  const requestedEngagementRef = useRef<string | null>(null);
  const bootstrapGenerationRef = useRef<number | null>(null);
  if (bootstrapGenerationRef.current === null) {
    bootstrapGenerationRef.current = ++requestGenerationRef.current;
  }
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // The SINGLE writer of both the React snapshot state AND the module-level
  // snapshot seam — they must never diverge (the readers read the module one).
  const publishSnapshot = useCallback((eng: Engagement | null) => {
    const next = eng ? snapshotOf(eng) : null;
    setEngagementSnapshot(next);
    if (mountedRef.current) setSnapshot(next);
  }, []);

  const adopt = useCallback(
    (eng: Engagement, generation: number) => {
      if (generation !== requestGenerationRef.current || requestedEngagementRef.current !== eng.id) return false;
      writePointer(eng.id);
      // Publish the snapshot FIRST so the module-level seam is set synchronously
      // BEFORE the currentEngagement state change re-renders the consumers. In
      // React 17 these async (post-await) state updates are unbatched, so a
      // consumer effect keyed on currentEngagementId would otherwise fire with the
      // module snapshot still stale (027 B2 — ScopeWidget re-seed ordering).
      publishSnapshot(eng);
      if (mountedRef.current) {
        setCurrentEngagement(eng);
        setIsFirstRun(false);
      }
      // 027 Slice D (FR-012): one-time, loss-free import of any legacy
      // arcpilot.{vision,orgContext,scope} into THIS engagement, then retire the
      // keys. The marker (arcpilot.imported:<id>) makes it idempotent, so a later
      // resolution / switch does NOT re-import. Writers are bound to eng.id
      // directly (the saveSub closure would still read the stale currentEngagement
      // immediately after adopt). Fire-and-forget + never-throw to preserve the
      // provider's resolve contract (it must never block/throw the resolve).
      const imported = snapshotOf(eng);
      const importWriter = async (
        sub: 'vision' | 'organisation' | 'streams',
        body: unknown,
      ): Promise<void> => {
        await putSubDoc(eng.id, sub, body);
        imported[sub] = body;
        if (generation === requestGenerationRef.current && requestedEngagementRef.current === eng.id) {
          publishSnapshot({ ...eng, ...imported } as Engagement);
        }
      };
      void importLegacyLocalStorage(eng.id, {
        saveVision: (vision) => importWriter('vision', vision),
        saveOrganisation: (organisation) => importWriter('organisation', organisation),
        saveStreams: (streams) => importWriter('streams', streams),
      }).catch(() => {
        // never-throw: a failed import must not break the resolve (the readers
        // keep their floor; the legacy keys simply stay until a future attempt).
      });
      return true;
    },
    [publishSnapshot],
  );

  // ── On mount: resolve the LIST and the CURRENT engagement ───────────────────
  useEffect(() => {
    let cancelled = false;
    const generation = bootstrapGenerationRef.current as number;
    const resolve = async () => {
      const list = (await getList()) ?? [];
      if (cancelled || !mountedRef.current) return;
      setEngagements(list);
      if (generation !== requestGenerationRef.current) return;

      // Zero engagements → first-run; no current; snapshot cleared so the
      // readers fall to their floor (the Slice C gateway handles the rest).
      if (list.length === 0) {
        setIsFirstRun(true);
        setCurrentEngagement(null);
        publishSnapshot(null);
        setLoading(false);
        return;
      }

      // Resolve the current engagement: the pointer if it is valid; else a
      // deterministic fallback to list[0] (and re-point to heal a stale /
      // missing pointer).
      const pointer = readPointer();
      const valid = pointer && list.some((e) => e.id === pointer);
      const targetId = valid ? (pointer as string) : list[0].id;
      requestedEngagementRef.current = targetId;

      const eng = await getById(targetId);
      if (cancelled || !mountedRef.current || generation !== requestGenerationRef.current) return;

      if (eng) {
        adopt(eng, generation);
      } else {
        // Could not load the resolved engagement → SAFE state (no current),
        // snapshot null so the readers keep their floor (mirrors
        // useVisionFraming's never-fabricate).
        clearPointer();
        setCurrentEngagement(null);
        publishSnapshot(null);
        setIsFirstRun(false);
      }
      setLoading(false);
    };
    resolve();
    return () => {
      cancelled = true;
    };
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── switchEngagement: re-point + swap the snapshot ──────────────────────────
  const switchEngagement = useCallback(
    async (id: string): Promise<boolean> => {
      const generation = ++requestGenerationRef.current;
      requestedEngagementRef.current = id;
      const eng = await getById(id);
      if (!eng || generation !== requestGenerationRef.current) {
        if (generation === requestGenerationRef.current && mountedRef.current) setLoading(false);
        return false;
      }
      const adopted = adopt(eng, generation);
      if (mountedRef.current) setLoading(false);
      return adopted;
    },
    [adopt],
  );

  // ── refreshEngagement: re-read the current engagement + re-publish snapshot ──
  // (041 F6) After a server-side vision capability mutates the engagement
  // aggregate, the form + chat re-read the one `vision` sub-doc via this refresh
  // (SC-003) rather than holding a second authoritative copy. A no-current /
  // failed re-read is a no-op (never throws — same degradation discipline).
  const refreshEngagement = useCallback(async (): Promise<void> => {
    const id = currentEngagement ? currentEngagement.id : readPointer();
    if (!id) return;
    if (requestedEngagementRef.current && requestedEngagementRef.current !== id) return;
    const generation = ++requestGenerationRef.current;
    requestedEngagementRef.current = id;
    const eng = await getById(id);
    if (eng) adopt(eng, generation);
  }, [currentEngagement, adopt]);

  // ── createEngagement: POST, becomes current, re-points ──────────────────────
  const createEngagement = useCallback(
    async (input: {
      client: string;
      programme?: string;
      title?: string;
      commandId?: string;
      flow?: 'brief';
    }): Promise<Engagement | null> => {
      const generation = ++requestGenerationRef.current;
      const created = await postCreate(input);
      if (!created) return null;
      if (generation !== requestGenerationRef.current) return null;
      requestedEngagementRef.current = created.id;
      if (mountedRef.current) {
        setEngagements((prev) => [
          ...prev,
          {
            id: created.id,
            client: created.client,
            programme: created.programme,
            title: created.title,
            lifecycle: created.lifecycle,
            activationState: created.activationState,
            createdAt: created.createdAt,
          },
        ]);
      }
      adopt(created, generation);
      return created;
    },
    [adopt],
  );

  // ── Save writers: PUT the sub-doc AND update the snapshot ───────────────────
  const saveSub = useCallback(
    async (
      sub: 'vision' | 'organisation' | 'streams',
      body: unknown,
    ): Promise<void> => {
      const eng = currentEngagement;
      if (!eng) return;
      const generation = requestGenerationRef.current;
      await putSubDoc(eng.id, sub, body);
      if (generation !== requestGenerationRef.current || requestedEngagementRef.current !== eng.id) return;
      const next: Engagement = { ...eng, [sub]: body };
      if (mountedRef.current) setCurrentEngagement(next);
      publishSnapshot(next);
    },
    [currentEngagement, publishSnapshot],
  );

  const saveVision = useCallback((vision: unknown) => saveSub('vision', vision), [saveSub]);
  const saveOrganisation = useCallback(
    (organisation: unknown) => saveSub('organisation', organisation),
    [saveSub],
  );
  const saveStreams = useCallback((streams: unknown) => saveSub('streams', streams), [saveSub]);

  const changeProcessSpine = useCallback(async (
    command: Record<string, unknown> & { expectedRevision: number },
  ): Promise<boolean> => {
    const eng = currentEngagement;
    if (!eng?.setupRevision) return false;
    if (requestedEngagementRef.current && requestedEngagementRef.current !== eng.id) return false;
    const generation = ++requestGenerationRef.current;
    requestedEngagementRef.current = eng.id;
    const result = await fetchJson<{ setup: unknown; revision: string }>(
      `/api/engagements/${encodeURIComponent(eng.id)}/setup/commands`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...command,
          commandId: processCommandId(),
          spineExpectedRevision: command.expectedRevision,
          expectedRevision: eng.setupRevision,
        }),
      },
    );
    if (!result) return false;
    const fresh = await getById(eng.id);
    if (!fresh) return false;
    adopt(fresh, generation);
    return true;
  }, [adopt, currentEngagement]);

  const value: UseEngagementReturn = {
    loading,
    engagements,
    currentEngagement,
    currentEngagementId: currentEngagement ? currentEngagement.id : null,
    snapshot,
    isFirstRun,
    createEngagement,
    switchEngagement,
    refreshEngagement,
    saveVision,
    saveOrganisation,
    saveStreams,
    changeProcessSpine,
  };

  return createElement(EngagementContext.Provider, { value }, children);
}

// A SAFE no-provider fallback (027 B2): consistent with the provider's
// never-throw degradation discipline. When a consumer/writer is rendered with NO
// EngagementProvider above it (e.g. a focused component test, or a surface not yet
// wrapped), useEngagement() resolves to this inert value rather than throwing —
// currentEngagementId is null, so writers fall back to their legacy localStorage
// home and the readers keep their empty-shape floor. (The save writers are inert
// here; with no engagement there is nothing to PUT.)
const NO_PROVIDER_FALLBACK: UseEngagementReturn = {
  loading: false,
  engagements: [],
  currentEngagement: null,
  currentEngagementId: null,
  snapshot: null,
  isFirstRun: false,
  createEngagement: async () => null,
  switchEngagement: async () => false,
  refreshEngagement: async () => {},
  saveVision: async () => {},
  saveOrganisation: async () => {},
  saveStreams: async () => {},
  changeProcessSpine: async () => false,
};

export function useEngagement(): UseEngagementReturn {
  const ctx = useContext(EngagementContext);
  return ctx ?? NO_PROVIDER_FALLBACK;
}
